import i18n from '@/utils/i18n';
import {
  Button,
  Dialog,
  InputCompound,
  XStack,
} from '@usepepper/sprout-kit-ui';
import {
  CollectionNameError,
  NAME_MAX_LENGTH,
  validateCollectionName,
} from '@/experiences/sales-portal/domain/order-guides/blocs/utils/naming-utils';
import { OrderGuide } from '@/experiences/sales-portal/domain/order-guides/blocs/types';
import { useCreateOrderGuideCollectionMutator } from '@/experiences/sales-portal/domain/order-guides/blocs/mutators/use-create-order-guide-collection-mutator';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useRef, useState } from 'react';
import { useTheme } from '@/contexts/theme-context';

interface CreateOrderGuideDialogContainerProps {
  existingNames: string[];
  isOpen: boolean;
  onCreated: (orderGuide: OrderGuide) => void;
  onOpenChange: (open: boolean) => void;
  restaurantUUID: string;
  supplierUUID: string;
}

const DIALOG_HEIGHT = 200;
const DIALOG_WIDTH = 420;
const NAME_INPUT_SIZE = 40;
const DIALOG_PADDING = 20;

const NAME_ERROR_MESSAGE_KEYS: Partial<Record<CollectionNameError, string>> = {
  duplicate:
    'sales_portal.order_guides_list.guide_selector.create_dialog.name_error_duplicate',
  reserved:
    'sales_portal.order_guides_list.guide_selector.create_dialog.name_error_reserved',
};

/**
 * Prompts the rep for a name, then creates a new order guide. A name is
 * required up front since collections can't be renamed after creation.
 */
export const CreateOrderGuideDialogContainer: React.FC<
  CreateOrderGuideDialogContainerProps
> = ({
  existingNames,
  isOpen,
  onCreated,
  onOpenChange,
  restaurantUUID,
  supplierUUID,
}) => {
  const theme = useTheme();
  const nameInputRef =
    useRef<React.ComponentRef<typeof InputCompound.Area>>(null);
  const [name, setName] = useState('');
  const [wasOpen, setWasOpen] = useState(isOpen);

  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setName('');
    }
  }

  const { showErrorToast } = useExperienceToasts();
  const { createOrderGuide, loading: isCreating } =
    useCreateOrderGuideCollectionMutator({
      onError: showErrorToast,
      restaurantUUID,
      supplierUUID,
    });

  const trimmedName = name.trim();
  const nameError = validateCollectionName(name, existingNames);
  const canSubmit = nameError === null && !isCreating;

  const nameErrorKey = nameError
    ? NAME_ERROR_MESSAGE_KEYS[nameError]
    : undefined;
  const nameErrorMessage = nameErrorKey ? i18n.t(nameErrorKey) : null;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const newOrderGuide = await createOrderGuide(trimmedName);
    if (!newOrderGuide) {
      return;
    }
    onOpenChange(false);
    onCreated(newOrderGuide);
  };

  const namePlaceholder = i18n.t(
    'sales_portal.order_guides_list.guide_selector.create_dialog.name_placeholder',
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={isOpen}>
      <Dialog.Content
        minHeight={DIALOG_HEIGHT}
        onOpenAutoFocus={(event: Event) => {
          // The trap would otherwise focus the header's close button.
          event.preventDefault();
          nameInputRef.current?.focus();
        }}
        paddingHorizontal={0}
        paddingVertical={DIALOG_PADDING}
        width={DIALOG_WIDTH}
      >
        <Dialog.Header paddingHorizontal={theme.sk.spacing.spaceMedium}>
          <Dialog.Title>
            {i18n.t(
              'sales_portal.order_guides_list.guide_selector.create_dialog.title',
            )}
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Body
          gap={theme.sk.spacing.spaceXsmall}
          paddingHorizontal={DIALOG_PADDING}
        >
          <InputCompound
            error={nameErrorMessage !== null}
            size={NAME_INPUT_SIZE}
          >
            <InputCompound.Box>
              <InputCompound.Section>
                <InputCompound.Area
                  accessibilityLabel={namePlaceholder}
                  maxLength={NAME_MAX_LENGTH}
                  onChangeText={setName}
                  onSubmitEditing={handleSubmit}
                  placeholder={namePlaceholder}
                  ref={nameInputRef}
                  value={name}
                />
              </InputCompound.Section>
            </InputCompound.Box>
          </InputCompound>
          {nameErrorMessage ? (
            <InputCompound.Info
              color={theme.sk.color.text.error}
              size="$sm"
              variant="body"
            >
              {nameErrorMessage}
            </InputCompound.Info>
          ) : null}
        </Dialog.Body>
        <Dialog.Footer
          paddingHorizontal={DIALOG_PADDING}
          paddingTop={theme.sk.spacing.spaceMedium}
        >
          <XStack gap={theme.sk.spacing.spaceSmall} justifyContent="flex-end">
            <Dialog.Close asChild>
              <Button variant="secondary">
                {i18n.t(
                  'sales_portal.order_guides_list.guide_selector.create_dialog.cancel',
                )}
              </Button>
            </Dialog.Close>
            <Button
              disabled={!canSubmit}
              loading={isCreating}
              onPress={handleSubmit}
            >
              {i18n.t(
                'sales_portal.order_guides_list.guide_selector.create_dialog.create',
              )}
            </Button>
          </XStack>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
};
